import { ApolloClient } from "@apollo/client"
import createContentfulClient from "@cms/createApolloClient"
import { GetDiscountedProductsData } from "@cms/graphql/queries"
import { formatInventoryData } from "@hooks/useMultiWarehouseInventory"
import { createModel } from "@rematch/core"
import { getAutomaticDiscounts, getInventoryByLocation } from "@utils/api"
import _ from "lodash-es"

import { AutomaticDiscountData, DiscountedProductsData } from "../types"
import { graphQLRequest } from "../utils"
import { RootModel } from "./index"

export interface DiscountsState {
  automaticDiscounts: AutomaticDiscountData[]
  automaticDiscountsFetched: boolean
  discountedProductsData: DiscountedProductsData
  discountedProductsInventoryData: any
}

const discounts = createModel<RootModel>()({
  effects: (dispatch: any) => ({
    async fetchAutomaticDiscounts() {
      const automaticDiscountsData = await getAutomaticDiscounts()
      dispatch.discounts.setAutomaticDiscounts(automaticDiscountsData || [])
      dispatch.discounts.setAutomaticDiscountsFetched(true)

      const contentfulProductIds: any = []

      if (automaticDiscountsData?.length > 0) {
        for (let i = 0; i < automaticDiscountsData.length; i++) {
          const { customerBuysProducts, customerGetsProducts, idMap } =
            automaticDiscountsData[i]

          if (customerGetsProducts?.length && idMap) {
            customerGetsProducts.forEach((shopifyId) => {
              contentfulProductIds.push(idMap[shopifyId])
            })
          }

          if (customerBuysProducts?.length && idMap) {
            customerBuysProducts.forEach((shopifyId) => {
              contentfulProductIds.push(idMap[shopifyId])
            })
          }
        }

        const filteredContentfulProductIds = contentfulProductIds.filter(
          (id) => !!id
        )

        if (filteredContentfulProductIds.length) {
          dispatch.discounts.fetchDiscountedProducts(
            filteredContentfulProductIds
          )
        }
      }
    },

    async fetchDiscountedProducts(ids) {
      const client: ApolloClient<any> = createContentfulClient()
      const batches = _.chunk(ids, 100)
      const batchItems: any = []

      for (let i = 0; i < batches.length; i++) {
        const { data } = await graphQLRequest(client.query, {
          query: GetDiscountedProductsData,
          variables: { ids: batches[i] },
        })

        if (data?.productCollection?.items) {
          batchItems.push(data?.productCollection?.items)
        }
      }

      if (batchItems.length > 0) {
        const items: any = _.flatten(batchItems)
        const formattedData = items.reduce((productMap, product) => {
          productMap[product.sys.id] = {
            brand: product.brand,
            builtInMagnet: product.builtInMagnet,
            bundleProduct: product.bundleProduct,
            contentful_id: product.sys.id,
            customPrint: product.customPrint,
            device: product.device,
            devices: null,
            productName: product.productName,
            productSubType: product.productSubType,
            shopifyProduct: product.shopifyProduct,
            slug: product.slug,
            variants: product.variantsCollection.items.map((v) => ({
              color: {
                hexString: v.color.hexString,
                name: v.color.name,
                swatchImage: v.color.swatchImage
                  ? {
                      file: {
                        url: v.color.swatchImage?.url?.replace("https:", ""),
                      },
                    }
                  : null,
              },
              compareAtPrice: v.compareAtPrice,
              contentful_id: v.sys.id,
              device: v.device,
              images: [
                {
                  file: {
                    url: v.imagesCollection.items[0]?.url?.replace(
                      "https:",
                      ""
                    ),
                  },
                },
              ],
              language: v.language,
              marketPrices: v.marketPrices,
              name: v.name,
              outOfStockMessage: v.outOfStockMessage,
              price: v.price,
              shopifyVariantId: v.shopifyVariantId,
              size: v.size,
              sku: v.sku,
            })),
          }

          return productMap
        }, {})

        dispatch.discounts.setDiscountedProductData(formattedData)
      }
    },

    /**
     * Fetch inventory for discount products relevant to the current PDP product
     * Only fetches inventory for products in discounts where the current product participates
     */
    async fetchDiscountInventoryForProduct(
      productContentfulId: string,
      state: any
    ) {
      const { automaticDiscounts, discountedProductsData } = state.discounts

      if (!automaticDiscounts?.length || !productContentfulId) {
        return
      }

      // Find discounts where this product participates (either as buy or get product)
      const relevantDiscounts = automaticDiscounts.filter((discount) => {
        const { customerBuysProducts, customerGetsProducts, idMap } = discount

        if (!idMap) return false

        // Check if current product is in buy products
        const isInBuyProducts = customerBuysProducts?.some(
          (shopifyId) => idMap[shopifyId] === productContentfulId
        )

        // Check if current product is in get products
        const isInGetProducts = customerGetsProducts?.some(
          (shopifyId) => idMap[shopifyId] === productContentfulId
        )

        return isInBuyProducts || isInGetProducts
      })

      if (!relevantDiscounts.length) {
        // No relevant discounts, clear inventory data
        dispatch.discounts.setDiscountedProductInventoryData({})
        return
      }

      // Collect all contentful IDs from relevant discounts
      const relevantContentfulIds = new Set<string>()

      relevantDiscounts.forEach((discount) => {
        const { customerGetsProducts, idMap } = discount

        // Only fetch inventory for products that can be discounted (customerGets)
        if (customerGetsProducts?.length && idMap) {
          customerGetsProducts.forEach((shopifyId) => {
            const contentfulId = idMap[shopifyId]
            if (contentfulId) {
              relevantContentfulIds.add(contentfulId)
            }
          })
        }
      })

      // Filter discountedProductsData to only relevant products
      const relevantProducts = Array.from(relevantContentfulIds)
        .map((id) => discountedProductsData[id])
        .filter(Boolean)

      if (!relevantProducts.length) {
        dispatch.discounts.setDiscountedProductInventoryData({})
        return
      }

      // Collect SKUs from relevant products
      const variantSkus = _.flatten(
        relevantProducts.map(({ variants }) => variants.map(({ sku }) => sku))
      )

      // Fetch inventory for relevant SKUs only
      const inventoryData = await getInventoryByLocation(variantSkus)
      const formattedInventoryData = inventoryData
        ? formatInventoryData(inventoryData)
        : {}

      // Merge with existing inventory data instead of replacing
      dispatch.discounts.mergeDiscountedProductInventoryData(
        formattedInventoryData
      )
    },
  }),
  name: "discounts",
  reducers: {
    mergeDiscountedProductInventoryData(
      state: DiscountsState,
      newInventoryData: any
    ) {
      return {
        ...state,
        discountedProductsInventoryData: {
          ...state.discountedProductsInventoryData,
          ...newInventoryData,
        },
      }
    },

    setAutomaticDiscounts(
      state: DiscountsState,
      automaticDiscounts: AutomaticDiscountData[]
    ) {
      return {
        ...state,
        automaticDiscounts,
      }
    },

    setAutomaticDiscountsFetched(
      state: DiscountsState,
      automaticDiscountsFetched: boolean
    ) {
      return {
        ...state,
        automaticDiscountsFetched,
      }
    },

    setDiscountedProductData(
      state: DiscountsState,
      discountedProductsData: DiscountedProductsData
    ) {
      return {
        ...state,
        discountedProductsData,
      }
    },

    setDiscountedProductInventoryData(
      state: DiscountsState,
      discountedProductsInventoryData: any
    ) {
      return {
        ...state,
        discountedProductsInventoryData,
      }
    },
  },
  // https://github.com/rematch/rematch/issues/816
  selectors: () => ({}),
  state: {
    automaticDiscounts: [] as AutomaticDiscountData[],
    automaticDiscountsFetched: false,
    discountedProductsData: {} as DiscountedProductsData,
    discountedProductsInventoryData: {} as any,
  } as DiscountsState,
})

export default discounts
